import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Folder,
  Kanban,
  ListTodo,
  Bug,
} from "lucide-react";
import JiraSidebar from "./JiraSidebar";

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default function JiraDashboard() {
  const [projects, setProjects] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch Projects
      const projRes = await fetch(`${API_BASE}/get_projects.php`);
      const projData = await projRes.json();
      setProjects(Array.isArray(projData) ? projData : []);

      // Fetch Issues
      const issueRes = await fetch(`${API_BASE}/get_issues.php`);
      const issueData = await issueRes.json();
      setIssues(Array.isArray(issueData) ? issueData : []);

      // Fetch Members
      const memberRes = await fetch(`${API_BASE}/get_members.php`);
      const memberData = await memberRes.json();
      setMembers(Array.isArray(memberData) ? memberData : []);

    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const activeIssues = issues.filter(i => i.status !== 'Done').length;
  const completedIssues = issues.filter(i => i.status === 'Done').length;
  const totalIssues = issues.length;
  const completionRate = totalIssues > 0 ? Math.round((completedIssues / totalIssues) * 100) : 0;

  return (
    <div className="flex flex-col flex-1 bg-[#f6f7fb]">

      {/* ================= BODY ================= */}
      <div className="flex flex-1 overflow-hidden">
        {/* ===== SIDEBAR ===== */}
        <JiraSidebar activeItem="jira" />

        {/* ===== MAIN ===== */}
        <main className="flex-1 p-6 overflow-y-auto">
          {/* Top Tabs */}
          <div className="flex gap-3 mb-6 text-sm relative z-20">
            <TopTab
              icon={<LayoutDashboard size={14} />}
              label="Dashboard"
              active
              onClick={() => (window.location.hash = "jira")}
            />

            <TopTab
              icon={<Folder size={14} />}
              label="Projects"
              onClick={() => (window.location.hash = "projects")}
            />

            <TopTab
              icon={<Kanban size={14} />}
              label="Board"
              onClick={() => (window.location.hash = "board")}
            />

            <TopTab
              icon={<ListTodo size={14} />}
              label="Backlog"
              onClick={() => (window.location.hash = "backlog")}
            />

            <TopTab
              icon={<Bug size={14} />}
              label="Issues"
              onClick={() => (window.location.hash = "issues")}
            />
          </div>

          <h1 className="text-xl font-semibold">Dashboard</h1>
          <p className="text-sm text-gray-500 mb-6">
            Overview of your projects and tasks
          </p>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Stat
              title="Total Projects"
              value={projects.length.toString()}
              color="green"
            />
            <Stat title="Active Issues" value={activeIssues.toString()} color="orange" />
            <Stat
              title="Completed"
              value={completedIssues.toString()}
              color="blue"
              sub={`${completionRate}% completion rate`}
            />
            <Stat title="Team Members" value={members.length.toString()} color="purple" />
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="Active Projects">
              {loading ? (
                <p className="text-gray-500 text-sm">Loading projects...</p>
              ) : projects.length > 0 ? (
                projects.map((proj) => (
                  <Project
                    key={proj.id}
                    tag={proj.project_key}
                    name={proj.name}
                    desc={proj.description}
                    percent={0}
                  />
                ))
              ) : (
                <p className="text-gray-500 text-sm">
                  No projects assigned to you yet.
                </p>
              )}
            </Card>

            <Card title="Recent Activity">
              {loading ? (
                <p className="text-gray-500 text-sm">Loading activity...</p>
              ) : issues.length > 0 ? (
                issues.slice(0, 5).map((issue) => (
                  <Activity
                    key={issue.id}
                    title={issue.type === 'B' ? 'Bug' : issue.type === 'S' ? 'Story' : 'Task'}
                    text={issue.summary}
                    time={new Date(issue.created_at).toLocaleDateString()}
                  />
                ))
              ) : (
                <Activity
                  title="System"
                  text="No recent activities found"
                  time=""
                />
              )}
            </Card>
          </div>
        </main>
      </div>

    </div>
  );
}
/* ================= COMPONENTS ================= */

interface TopTabProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}

function TopTab({ icon, label, active, onClick }: TopTabProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-md select-none
      ${active
          ? "bg-green-500 text-white"
          : "bg-white border text-gray-700 hover:bg-gray-100"
        }`}
    >
      {icon}
      {label}
    </button>
  );
}

interface StatProps {
  title: string;
  value: string;
  color: "green" | "orange" | "blue" | "purple";
  sub?: string;
}

function Stat({ title, value, color, sub }: StatProps) {
  const colors = {
    green: "border-green-500",
    orange: "border-orange-500",
    blue: "border-blue-500",
    purple: "border-purple-500",
  };

  return (
    <div className={`bg-white border-l-4 ${colors[color]} rounded-lg p-4`}>
      <p className="text-xs text-gray-500">{title}</p>
      <p className="text-2xl font-semibold">{value}</p>
      {sub && <p className="text-xs text-green-500 mt-1">{sub}</p>}
    </div>
  );
}

interface CardProps {
  title: string;
  children: React.ReactNode;
}

function Card({ title, children }: CardProps) {
  return (
    <div className="bg-white rounded-lg p-4">
      <h3 className="font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

interface ProjectProps {
  tag: string;
  name: string;
  desc: string;
  percent: number;
}

function Project({ tag, name, desc, percent }: ProjectProps) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
          {tag}
        </span>
        <span className="font-medium">{name}</span>
      </div>
      <p className="text-xs text-gray-500 mb-2">{desc}</p>
      <div className="w-full h-2 bg-gray-200 rounded-full">
        <div
          className="h-2 bg-green-500 rounded-full"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

interface ActivityProps {
  title: string;
  text: string;
  time: string;
}

function Activity({ title, text, time }: ActivityProps) {
  return (
    <div className="mb-3">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-gray-600">{text}</p>
      <p className="text-xs text-gray-400">{time}</p>
    </div>
  );
}
